import {Item} from "./Item";
import {Address} from "../Address";
import {Facility} from "../Facility";
import {ShippingItem} from "./ShippingItem";
import {ShippingPackage} from "./ShippingPackage";
import {PackageAndShippingComputationService} from "../../app/service-data/package-and-shipping-computation.service";
import {Cart} from "./Cart";
import {ShippingOption} from "./ShippingOption";
import {environment} from "../../environments/environment";

export class PrintNodeOrderOption {
  carrier: string;
  serviceLevel: string;
  packItemsInSeparateBoxes: boolean;
  useCaseQuantities: boolean;
  deliveryAddress: Address;
  facilityId: number;
  items: Item[] = [];
  shippingPackages: ShippingPackage[] = [];
  packageAndShippingComputationService: PackageAndShippingComputationService;
  computationStatus = 'DIRTY';
  computationVersion: number = 1;

  constructor (carrier: string, serviceLevel: string, deliveryAddress: Address, facilityId: number, packItemsInSeparateBoxes: boolean, useCaseQuantities: boolean, packageAndShippingComputationService: PackageAndShippingComputationService) {
    this.carrier = carrier;
    this.serviceLevel = serviceLevel;
    this.deliveryAddress = deliveryAddress;
    this.facilityId = facilityId;
    this.packItemsInSeparateBoxes = packItemsInSeparateBoxes;
    this.useCaseQuantities = useCaseQuantities;
    this.packageAndShippingComputationService = packageAndShippingComputationService;
  }

  addItem(item: Item) {
      this.items.push(item);
      this.computationStatus = 'DIRTY';
  }

  getShippingItems(): ShippingItem[] {
    const shippingItems: ShippingItem[] = [];
    for ( const item of this.items ) {
      // ShippingItem constructor signature is (height, width, depth,
      // thickness, weight, quantity, caseQuantity, manufacturingSetId).
      // Historical wire convention (unchanged since before 2026):
      //   - `height` slot receives `item.getWidth()` (book's short dim)
      //   - `width`  slot receives `item.getHeight()` (book's tall dim)
      //   - `depth`  = per-book thickness in inches
      //   - `thickness` = stack height for `quantity` books
      //     (`getThickness() * quantity`)
      // The server-side packer keys on these positions; changing the
      // apparent width/height swap would break the packing computation.
      // Preserved verbatim — only the depth/thickness units and the
      // per-item completeness gate (see `compute()` below) needed
      // fixing to resolve the mixed-units / zero-dimension bug.
      const perBookThickness = item.getThickness();
      shippingItems.push(new ShippingItem(
        item.getWidth(),
        item.getHeight(),
        perBookThickness,
        perBookThickness * item.quantity,
        item.getWeight(),
        item.quantity,
        item.getCaseQuantity()
      ));
    }
    return shippingItems;
  }

  compute(cart: Cart) {
    if ( this.computationStatus  === 'DIRTY') {
      // Defer the getPackages call when any assigned item still has
      // asynchronously-linked data outstanding (e.g. a Product whose
      // `book.body` hasn't attached yet, or a Draft whose `draftBody` /
      // `draftCover` is still in flight). Firing now would send zero-
      // valued dimensions / thickness that produce meaningless boxes.
      // Leave status at DIRTY (rather than transitioning to COMPUTING)
      // so a later call — the cart-page's per-service add-notifier
      // subscriptions call `Cart.compute()` again when linked rows
      // land — will re-enter this branch and fire the request with
      // the real geometry.
      for (const item of this.items) {
        if (typeof (item as any).hasCompleteRateInputs === 'function'
            && !(item as any).hasCompleteRateInputs()) {
          // Diagnostic: this is the second way the checkout can sit on
          // "Getting shipping options" with an empty network log (the
          // first — an empty `Cart.orderOptions` — was fixed in
          // `Cart.compute` on 2026-08-14). Here the request is
          // deliberately deferred until the item's linked rows land; if
          // they never do, this log is the only breadcrumb.
          console.warn(
            'PrintNodeOrderOption.compute: deferring shipping request — item still missing rate inputs',
            { facilityId: this.facilityId, itemId: item.getId(), title: item.getTitle() }
          );
          return;
        }
      }
      this.computationStatus = 'COMPUTING';
      this.computationVersion ++;
      this.shippingPackages = [];
      this.packageAndShippingComputationService.getPackages(this.carrier, this.serviceLevel, this.facilityId, this.getShippingItems(), this.packItemsInSeparateBoxes, this.useCaseQuantities, this.computationVersion).subscribe( shippingPackagesResponse => {
        if ( shippingPackagesResponse['status'] = 'SUCCESS' && shippingPackagesResponse && shippingPackagesResponse['packages'] && shippingPackagesResponse['packages'].length > 0 ) {
          if ( shippingPackagesResponse['computationVersion'] === this.computationVersion ) { // Only use the result if it is the current version
            this.shippingPackages = [];
            shippingPackagesResponse['packages'].forEach( incommingShippingPackage => {
              // Forward the server-supplied identifiers/metrics so the
              // follow-up `getShippingOptions` call can echo `packageTypeId`
              // back (the rating service keys on it, and packages without a
              // type produce a "no matching package type" error / mis-rated
              // shipment). `itemsPerPackage` and `packingCost` are also
              // preserved here so per-package packing totals stay accurate
              // even before the rate call resolves. `packageIndex` is
              // preserved for parity with the newer combined rateshipping
              // response shape. See ShippingPackage.ts and the response
              // shape mirrored in Cart.ts (`rateShippingByFacility`).
              //
              // The `packageandshippingcomputation/packages` endpoint pre-
              // dates the 2026-06-24 rateshipping wire cleanup, so the
              // packer response may spell the id either as camelCase
              // `packageTypeId` (new / preferred, matches
              // `docs/contracts/shared/RateShipping.md`) OR the legacy
              // snake-with-underscore `packageType_ID` used by the
              // `Package` model and every pre-2026 wire message
              // (see `docs/contracts/shared/ChangePackages.md`). Accept
              // either so the follow-up rating call always echoes a
              // populated id. If neither is present, log a diagnostic —
              // the rating service can still fall back to dims-only, but
              // that produces mis-rated shipments and is a server bug we
              // want to spot immediately.
              const rawPkg: any = incommingShippingPackage;
              const packageTypeId = rawPkg.packageTypeId !== undefined
                ? rawPkg.packageTypeId
                : rawPkg.packageType_ID;
              if (packageTypeId === undefined || packageTypeId === null) {
                console.error(
                  'PrintNodeOrderOption.compute: packer response missing packageTypeId / packageType_ID',
                  { facilityId: this.facilityId, package: rawPkg }
                );
              }
              this.shippingPackages.push(new ShippingPackage(
                rawPkg.height,
                rawPkg.width,
                rawPkg.depth,
                rawPkg.weight,
                rawPkg.quantity,
                [],
                rawPkg.itemsPerPackage,
                rawPkg.packingCost,
                packageTypeId,
                rawPkg.packageIndex
              ));
            });
            cart.shippingStatus = 'READY'; // this is just here temporarily for debugging the results
            for ( const shippingPackage of this.shippingPackages ) {
              shippingPackage.computationStatus = 'COMPUTING';
              this.packageAndShippingComputationService.getShippingOptions(this.carrier, this.serviceLevel, this.facilityId, this.deliveryAddress, shippingPackage, this.computationVersion).subscribe( response => {
                if ( response['status'] === 'SUCCESS' ) {
                  const shippingOptions = [];
                  response['shippingOptions'].forEach( incommingShippingOption => {
                    // Forward every field the server may emit for this rate row.
                    // Transit estimate: prefer the range fields
                    // (`deliveryDaysMin` / `deliveryDaysMax` — added 2026-07-08
                    // per the outbox request `2026-07-08-shippingoption-transit-days.md`)
                    // but keep decoding the legacy single-number `deliveryDays`
                    // so pre-cutover responses still populate a range via
                    // `ShippingOption.getDeliveryDaysRange`'s fallback.
                    //
                    // Cost field: prefer canonical `cost`, but tolerate
                    // the alternate spellings the server has historically
                    // used on adjacent endpoints (`totalCost`, `amount`,
                    // `price`) so a rate row whose numeric column is
                    // labelled differently by the underlying carrier
                    // adapter still surfaces a value rather than
                    // silently rendering as $0.00 in the picker.
                    const raw: any = incommingShippingOption;
                    let cost = raw.cost;
                    if (cost === undefined || cost === null) { cost = raw.totalCost; }
                    if (cost === undefined || cost === null) { cost = raw.amount; }
                    if (cost === undefined || cost === null) { cost = raw.price; }
                    if (cost === undefined || cost === null) { cost = 0; }
                    if (typeof cost === 'string') { cost = parseFloat(cost); }
                    if (!environment.production && (!raw.cost && raw.cost !== 0)) {
                      // eslint-disable-next-line no-console
                      console.warn('[PrintNodeOrderOption] shippingOption missing `cost` — using fallback', { raw, resolvedCost: cost });
                    }
                    shippingOptions.push(new ShippingOption(
                      raw.carrier,
                      raw.serviceLevel,
                      cost,
                      raw.deliveryDays,
                      undefined,
                      undefined,
                      raw.deliveryDaysMin,
                      raw.deliveryDaysMax
                    ));
                  });
                  shippingPackage.setShippingOptions(shippingOptions);
                  shippingPackage.computationStatus = 'COMPLETE';
                  cart.checkComputationStatus();
                } else {
                  // Show that the response had an error rather than just waiting forever and doing nothing
                }
              });
            }
          } else {
            // Ignoring response because it is no longer the current set of items
          }
        } else {
          // Trouble
        }
      });
    } else {
      // Either COMPUTING and or COMPLETE
    }
  }

  checkComputationStatus(): string {
    if ( this.computationStatus === 'INITIAL') {
      return 'INITIAL';
    }
    if ( this.computationStatus === 'ERROR') {
      return 'ERROR';
    }
    let cs = '';
    this.shippingPackages.forEach( shippingPackage => {
      if ( shippingPackage.computationStatus === 'ERROR') {
        this.computationStatus = 'ERROR';
        return this.computationStatus;
      }
      if ( shippingPackage.computationStatus === 'COMPUTING' ) {
        cs = 'COMPUTING';
      }
    });
    if ( cs.length === 0) {
      this.computationStatus = 'COMPLETE';
    } else {
      this.computationStatus = 'COMPUTING';
    }
    return this.computationStatus;
  }


  getTotalPackages() {
    let totalPackages = 0;
    if ( this.computationStatus === 'COMPLETE') {
      for ( const shippingPackage of this.shippingPackages ) {
        totalPackages += shippingPackage.quantity;
      }
      return totalPackages;
    } else {
      return -1;
    }
  }

  /**
   * Total number of books being shipped from this facility.
   * Sum of every item's quantity across all items assigned to this PNOO.
   */
  getTotalBookCount(): number {
    let totalBooks = 0;
    for (const item of this.items) {
      totalBooks += item.quantity || 0;
    }
    return totalBooks;
  }

  /**
   * Total packing/packaging cost across all packages for this facility.
   * Each package contributes its own cost (server-supplied or per-book fallback).
   * If the server has not supplied per-package `itemsPerPackage`, the
   * per-package fallback would only count one book per package; in that case
   * we instead apply the default rate to the total book count for this PNOO.
   */
  getTotalPackingCost(): number {
    if (!this.shippingPackages || this.shippingPackages.length === 0) {
      // No packages computed yet — estimate from total books.
      return ShippingPackage.DEFAULT_PACKING_COST_PER_BOOK * this.getTotalBookCount();
    }
    let serverProvidedAny = false;
    let anyHasItemsPerPackage = false;
    let total = 0;
    for (const shippingPackage of this.shippingPackages) {
      if (shippingPackage.packingCost !== undefined && shippingPackage.packingCost !== null) {
        serverProvidedAny = true;
      }
      if (shippingPackage.itemsPerPackage && shippingPackage.itemsPerPackage > 0) {
        anyHasItemsPerPackage = true;
      }
      total += shippingPackage.getPackingCost();
    }
    // When server gave no per-package costs and no items-per-package counts,
    // the per-package fallback has no information to scale to the real book
    // count, so use the cart-level fallback instead.
    if (!serverProvidedAny && !anyHasItemsPerPackage) {
      return ShippingPackage.DEFAULT_PACKING_COST_PER_BOOK * this.getTotalBookCount();
    }
    return total;
  }

  getTotalPackingAndShippingCost(carrier: string, serviceLevel: string) {
    let totalCost = 0;
    if ( this.computationStatus === 'COMPLETE') {
      for ( const shippingPackage of this.shippingPackages ) {
        totalCost += shippingPackage.getCost(carrier, serviceLevel);
      }
      return totalCost;
    } else {
      return -1;
    }
  }
}

